import React, { useState, useEffect } from 'react';
import { Activity, Database, RefreshCw, TrendingUp, BarChart3 } from 'lucide-react';
import { useMarketData } from '../contexts/MarketDataContext';

const StockIndexTestPage: React.FC = () => {
  const { marketData, isConnected } = useMarketData();
  const [messages, setMessages] = useState<string[]>([]);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const testStocks = [
    { symbol: 'AAPL', name: 'Apple Inc.' },
    { symbol: 'MSFT', name: 'Microsoft' },
    { symbol: 'GOOGL', name: 'Alphabet' },
    { symbol: 'AMZN', name: 'Amazon' },
    { symbol: 'TSLA', name: 'Tesla' },
    { symbol: 'NVDA', name: 'NVIDIA' },
    { symbol: 'META', name: 'Meta' },
    { symbol: 'NFLX', name: 'Netflix' }
  ];

  const testIndices = [
    { symbol: 'SPX', name: 'S&P 500' },
    { symbol: 'DJI', name: 'Dow Jones' },
    { symbol: 'IXIC', name: 'NASDAQ' },
    { symbol: 'RUT', name: 'Russell 2000' },
    { symbol: 'VIX', name: 'VIX' },
    { symbol: 'FTSE', name: 'FTSE 100' },
    { symbol: 'N225', name: 'Nikkei 225' },
    { symbol: 'HSI', name: 'Hang Seng' }
  ];

  const addMessage = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setMessages(prev => [`[${timestamp}] ${message}`, ...prev.slice(0, 49)]);
  };

  const getStockData = () => {
    const stockData: any[] = [];
    testStocks.forEach(stock => {
      const data = marketData.find(m => m.symbol === stock.symbol);
      if (data) {
        stockData.push({ ...data, name: stock.name });
      }
    });
    return stockData;
  };

  const getIndexData = () => {
    const indexData: any[] = [];
    testIndices.forEach(index => {
      const data = marketData.find(m => m.symbol === index.symbol);
      if (data) {
        indexData.push({ ...data, name: index.name });
      }
    });
    return indexData;
  };

  const checkAllData = () => {
    addMessage('🔍 Checking all market data...');
    addMessage(`📊 Total market data entries: ${marketData.length}`);

    const stockData = getStockData();
    const indexData = getIndexData();

    addMessage(`📈 Stocks found: ${stockData.length}/${testStocks.length}`);
    addMessage(`📊 Indices found: ${indexData.length}/${testIndices.length}`);

    if (stockData.length > 0) {
      stockData.forEach(stock => {
        addMessage(`✅ Stock: ${stock.symbol} = $${stock.price?.toFixed(2) || '0.00'} (${stock.change_24h?.toFixed(2) || '0.00'}%)`);
      });
    } else {
      addMessage('❌ No stock data found in market_data');
    }

    if (indexData.length > 0) {
      indexData.forEach(index => {
        addMessage(`✅ Index: ${index.symbol} = ${index.price?.toFixed(2) || '0.00'} (${index.change_24h?.toFixed(2) || '0.00'}%)`);
      });
    } else {
      addMessage('❌ No index data found in market_data');
    }

    // Show a sample of what IS in market_data
    addMessage('📋 Sample of symbols in market_data:');
    const sample = marketData.slice(0, 10);
    sample.forEach(item => {
      addMessage(`  - ${item.symbol}: $${item.price?.toFixed(2) || '0.00'}`);
    });
  };

  const showAllMarketData = () => {
    addMessage('📊 All market data symbols:');
    const grouped: Record<string, number> = {};

    marketData.forEach(item => {
      // Categorize by symbol pattern
      if (item.symbol.includes('USD') || item.symbol.includes('EUR') || item.symbol.includes('GBP')) {
        grouped['Forex'] = (grouped['Forex'] || 0) + 1;
      } else if (item.symbol.includes('XAU') || item.symbol.includes('XAG') || item.symbol.includes('WTI')) {
        grouped['Commodities'] = (grouped['Commodities'] || 0) + 1;
      } else if (item.symbol.length <= 5 && item.symbol === item.symbol.toUpperCase()) {
        grouped['Stocks/Indices'] = (grouped['Stocks/Indices'] || 0) + 1;
      } else {
        grouped['Crypto'] = (grouped['Crypto'] || 0) + 1;
      }
    });

    Object.entries(grouped).forEach(([category, count]) => {
      addMessage(`  ${category}: ${count} symbols`);
    });

    // Show specific stocks and indices that ARE present
    const stocksPresent = testStocks.filter(s => marketData.some(m => m.symbol === s.symbol));
    const indicesPresent = testIndices.filter(i => marketData.some(m => m.symbol === i.symbol));

    addMessage(`\n📈 Test stocks present: ${stocksPresent.map(s => s.symbol).join(', ') || 'NONE'}`);
    addMessage(`📊 Test indices present: ${indicesPresent.map(i => i.symbol).join(', ') || 'NONE'}`);
  };

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        checkAllData();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, marketData]);

  const stockData = getStockData();
  const indexData = getIndexData();

  return (
    <div className="min-h-screen p-8 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
              <Activity size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">
                Stock & Index Data Test
              </h1>
              <p className="text-slate-400">Debugging stocks and indices from market_data</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${
              isConnected ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
            }`}>
              <Database size={16} />
              <span className="text-sm font-medium">
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>

            <button
              onClick={checkAllData}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg flex items-center gap-2"
            >
              <RefreshCw size={18} />
              Check Data
            </button>

            <button
              onClick={showAllMarketData}
              className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg flex items-center gap-2"
            >
              <BarChart3 size={18} />
              Show All Data
            </button>

            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`${autoRefresh ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-500 hover:bg-emerald-600'} text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg`}
            >
              {autoRefresh ? 'Stop Auto' : 'Start Auto'}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="text-slate-400 text-sm mb-1">Total Market Data</div>
            <div className="text-2xl font-bold text-white">{marketData.length}</div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="text-slate-400 text-sm mb-1">Stocks Found</div>
            <div className="text-2xl font-bold text-emerald-400">{stockData.length}/{testStocks.length}</div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="text-slate-400 text-sm mb-1">Indices Found</div>
            <div className="text-2xl font-bold text-cyan-400">{indexData.length}/{testIndices.length}</div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="text-slate-400 text-sm mb-1">Connection</div>
            <div className={`text-2xl font-bold ${isConnected ? 'text-emerald-400' : 'text-red-400'}`}>
              {isConnected ? 'Live' : 'Offline'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Stocks */}
          <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp size={20} className="text-emerald-400" />
              <h2 className="text-xl font-semibold text-white">Stocks</h2>
              <span className="text-xs text-slate-400 bg-slate-700/50 px-2 py-1 rounded-full">
                {stockData.length}/{testStocks.length}
              </span>
            </div>

            <div className="space-y-3">
              {testStocks.map((stock) => {
                const data = stockData.find(s => s.symbol === stock.symbol);
                return (
                  <div
                    key={stock.symbol}
                    className={`bg-slate-900/40 rounded-xl p-3 border ${
                      data ? 'border-emerald-500/30' : 'border-slate-700/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-white">{stock.symbol}</div>
                        <div className="text-xs text-slate-400">{stock.name}</div>
                      </div>
                      {data ? (
                        <div className="text-right">
                          <div className="text-white font-mono">${data.price?.toFixed(2) || '0.00'}</div>
                          <div className={`text-xs ${data.change_24h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {data.change_24h >= 0 ? '+' : ''}{data.change_24h?.toFixed(2) || '0.00'}%
                          </div>
                        </div>
                      ) : (
                        <div className="text-slate-500 text-sm">No data</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Indices */}
          <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50">
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 size={20} className="text-cyan-400" />
              <h2 className="text-xl font-semibold text-white">Indices</h2>
              <span className="text-xs text-slate-400 bg-slate-700/50 px-2 py-1 rounded-full">
                {indexData.length}/{testIndices.length}
              </span>
            </div>

            <div className="space-y-3">
              {testIndices.map((index) => {
                const data = indexData.find(i => i.symbol === index.symbol);
                return (
                  <div
                    key={index.symbol}
                    className={`bg-slate-900/40 rounded-xl p-3 border ${
                      data ? 'border-cyan-500/30' : 'border-slate-700/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-white">{index.symbol}</div>
                        <div className="text-xs text-slate-400">{index.name}</div>
                      </div>
                      {data ? (
                        <div className="text-right">
                          <div className="text-white font-mono">{data.price?.toFixed(2) || '0.00'}</div>
                          <div className={`text-xs ${data.change_24h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {data.change_24h >= 0 ? '+' : ''}{data.change_24h?.toFixed(2) || '0.00'}%
                          </div>
                        </div>
                      ) : (
                        <div className="text-slate-500 text-sm">No data</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Log */}
          <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Debug Log</h2>
              <button
                onClick={() => setMessages([])}
                className="text-slate-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-slate-700/50"
              >
                <RefreshCw size={16} />
              </button>
            </div>

            <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 h-[600px] overflow-y-auto">
              <div className="space-y-1 font-mono text-xs">
                {messages.length > 0 ? (
                  messages.map((message, index) => (
                    <div
                      key={index}
                      className={`${
                        message.includes('✅') ? 'text-emerald-400' :
                        message.includes('❌') ? 'text-red-400' :
                        message.includes('📊') || message.includes('📈') ? 'text-cyan-400' :
                        message.includes('🔍') ? 'text-amber-400' :
                        'text-slate-300'
                      }`}
                    >
                      {message}
                    </div>
                  ))
                ) : (
                  <div className="text-slate-500 text-center py-8">
                    No messages yet. Click "Check Data" to start.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockIndexTestPage;
